import React, { useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import useToast from 'hooks/useToast';
import { useAuthContext } from 'contexts/AuthContext';
import { Deal } from 'views/Deals/types';
import { useUpdateDeal } from 'components/DealForm/hooks/useUpdateDeal';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import {
    formatUSDCurrency,
    formatDateShort,
    formatDecimalAsPercentage,
    numeralFormatter,
} from 'ns_libs/formatter';
import { useGetAttributes } from 'components/DealForm/hooks/useGetAttributes';
import { useProjectActualsForDeal } from 'hooks/useGetProjectActuals';
import DealDetails, { IDetailItem } from '../../../components/DealDetails/DealDetails';
import Timeline from './Timeline/Timeline';
import DealReturns, { TargetReturnsValues } from './DealReturns/DealReturns';

interface IOverviewProps {
    deal: Deal;
}

const Overview = ({ deal }: IOverviewProps) => {
    const { showSuccess, showError } = useToast();
    const { selectedOrganizationId } = useAuthContext();
    const { data: attributesConfig } = useGetAttributes();

    const {
        data: projectActualsData,
    } = useProjectActualsForDeal({
        dealId: deal.id,
    });

    const customAttributesDetailsArray = useMemo(
        () => deal.customAttributes
            ?.filter(customAttribute => attributesConfig?.find(attr => attr.correlationId === customAttribute.attributeId))
            ?.map((customAttribute, index) => {
                const attribute = attributesConfig?.find(attr => attr.correlationId === customAttribute.attributeId);
                const value
                    = customAttribute.valueText
                    || customAttribute.valueNumber
                    || customAttribute.valueSingleSelect
                    || customAttribute.valueMultiSelect?.join(', ')
                    || '';
                return {
                    id: 8000 + index,
                    label: attribute!.name,
                    value,
                };
            }) || [],
        [deal, attributesConfig],
    );

    const { mutate: updateDeal } = useUpdateDeal();
    const { dealId } = useParams<{ dealId: string }>();

    const detailsArray = useMemo(() => {
        const details: IDetailItem[] = [
            { id: 1, label: 'Address', value: deal.address },
            { id: 2, label: 'City', value: deal.city },
            { id: 3, label: 'County', value: deal.county },
            { id: 4, label: 'State', value: deal.state },
            { id: 5, label: 'Zip code', value: deal.zipCode },
            { id: 6, label: 'Market', value: deal.market?.name || '' },
            { id: 7, label: 'Submarket', value: deal.submarket },
            { id: 8, label: 'Creation date', value: formatDateShort(deal.createdAt) },
            { id: 9, label: 'Description', value: deal.description },
            { id: 10, label: 'Deal type', value: deal.dealType?.name },
            { id: 11, label: 'Asset type', value: deal.assetType?.name || '' },
            { id: 12, label: 'Construction type', value: deal.constructionType?.name || '' },
            { id: 13, label: 'Parking type', value: deal.parkingType || '' },
            { id: 14, label: 'Built gross SF', value: deal.grossBuildSf },
            { id: 15, label: 'Rentable SF', value: deal.rentableSf },
            { id: 16, label: 'Acres', value: deal.acres },
            { id: 17, label: 'Residential units', value: deal.residentialUnits },
            { id: 18, label: 'Beds', value: deal.beds },
            { id: 19, label: 'Lots', value: deal.lots },
            { id: 20, label: 'Megawatts', value: deal.megawatts },
            { id: 21, label: 'Phase', value: deal?.subprojectStage?.name },
            { id: 22, label: 'Lead source', value: deal?.leadSource?.name },
            { id: 23, label: 'Leasing strategy', value: deal?.leasingStrategy?.name },
            { id: 24, label: 'Rank', value: deal?.rank?.name },
            { id: 25, label: 'Acquisition price', value: formatUSDCurrency(deal.acquisitionPrice, true) },
            { id: 26, label: 'IRR', value: deal.irr },
            {
                id: 27,
                label: 'IRR (using project actuals)',
                value: projectActualsData?.returns?.netLeveredIrr
                    ? formatDecimalAsPercentage(
                        projectActualsData.returns.netLeveredIrr,
                        2,
                    )
                    : undefined,
            },
            { id: 28, label: 'Equity multiple', value: deal.equityMultiple },
            {
                id: 29,
                label: 'Equity multiple (using project actuals)',
                value: projectActualsData?.returns?.netLeveredEquityMultiple
                    ? `${numeralFormatter(
                        projectActualsData.returns.netLeveredEquityMultiple,
                        '0.00',
                    )}x`
                    : undefined,
            },
            { id: 30, label: 'Created by', value: deal.creatorName },
            { id: 31, label: 'Deal owner', value: deal?.dealOwner?.name || '' },
            { id: 32, label: 'Development manager', value: deal?.developmentManager?.name },
            { id: 33, label: 'Investor - LP', value: deal.investorLp?.name },
            { id: 34, label: 'Lender (private)', value: deal.lenderPrivate?.name },
            { id: 35, label: 'Legal services', value: deal.legalServices?.name },
            { id: 36, label: 'Project architect', value: deal.projectArchitect?.name },
            { id: 37, label: 'Surveyor', value: deal.surveyor?.name },
            { id: 38, label: 'Environmental consultant', value: deal.environmentalConsultant?.name },
            ...customAttributesDetailsArray,
        ];

        // if asset subtype is present, add it after the asset type
        if (deal.assetSubtype?.description) {
            const index = details.findIndex(item => item.label === 'Asset type');
            if (index !== -1) {
                details.splice(index + 1, 0, {
                    id: 0,
                    label: '',
                    value: deal.assetSubtype.description,
                    valueClassName: 'text-muted',
                    removeTopMargin: true,
                });
            }
        }

        return details;
    }, [deal, customAttributesDetailsArray, projectActualsData]);

    const { refetch } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const handleTargetReturnsSave = (values: TargetReturnsValues) => {
        updateDeal(
            {
                orgId: selectedOrganizationId!,
                dealId: Number(deal.id),
                postData: {
                    targetProfitReturn: values.profit,
                    targetReturnOnCost: values.cost,
                    targetReturnOnEquity: values.equity,
                },
            },
            {
                onSuccess: data => {
                    console.log('Target returns updated successfully:', data);
                    showSuccess('Target returns updated successfully!');
                    refetch();
                },
                onError: error => {
                    console.error('Error updating deal:', error);
                    showError('Failed to update target returns. Please try again.');
                },
            },
        );
    };

    return (
        <Row className="g-0 h-100">
            <Col md="4" className="d-flex flex-column h-100 border-end">
                <DealDetails deal={deal} detailsArray={detailsArray} />
            </Col>

            <Col md="8" className="d-flex flex-column h-100">
                <div className="flex-shrink-0 mt-3">
                    <DealReturns
                        onSave={handleTargetReturnsSave}
                        deal={deal}
                    />
                </div>
                <div className="flex-grow-1 mt-3">
                    <Timeline dealId={deal.id} />
                </div>
            </Col>
        </Row>
    );
};

export default Overview;
